import React, { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import toast from "react-hot-toast";
import { createForumComment, getForumComments, getForumPost, resolveForumImageUrl, toggleForumLike } from "../../api/axios";
import { doctorImageUrl } from "../../api/imageUrl";
import { useAuth } from "../../auth/AuthContext";
import { timeAgo } from "../../utils/timeAgo";

const TYPE_STYLES = {
  question: "bg-blue-50 text-blue-700 border-blue-100",
  case_report: "bg-purple-50 text-purple-700 border-purple-100",
  image: "bg-pink-50 text-pink-700 border-pink-100",
  discussion: "bg-amber-50 text-amber-700 border-amber-100",
};

function Avatar({ name, image, size = 11 }) {
  const src = doctorImageUrl(image);
  const cls = `w-${size} h-${size} rounded-full shrink-0`;
  if (src) {
    return <img src={src} alt={name} className={`${cls} object-cover border border-gray-100`} onError={(e) => { e.target.style.display = "none"; }} />;
  }
  return (
    <div className={`${cls} bg-[#00342b] text-white flex items-center justify-center font-bold`}>
      {(name || "?").trim().charAt(0).toUpperCase()}
    </div>
  );
}

const HeartIcon = ({ filled }) => (
  <svg className="w-5 h-5" viewBox="0 0 24 24" fill={filled ? "currentColor" : "none"} stroke="currentColor" strokeWidth={2}>
    <path d="M20.84 4.61a5.5 5.5 0 00-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 10-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 000-7.78z" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

const EyeIcon = () => (
  <svg className="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2}>
    <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" strokeLinecap="round" strokeLinejoin="round" />
    <circle cx="12" cy="12" r="3" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

const CommentIcon = () => (
  <svg className="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2}>
    <path d="M21 11.5a8.38 8.38 0 01-.9 3.8 8.5 8.5 0 01-7.6 4.7 8.38 8.38 0 01-3.8-.9L3 21l1.9-5.7a8.38 8.38 0 01-.9-3.8 8.5 8.5 0 014.7-7.6 8.38 8.38 0 013.8-.9h.5a8.48 8.48 0 018 8v.5z" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

export default function ForumDetail() {
  const { id } = useParams();
  const { user } = useAuth();
  const [post, setPost] = useState(null);
  const [comments, setComments] = useState([]);
  const [comment, setComment] = useState("");
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    (async () => {
      const [p, c] = await Promise.all([getForumPost(id), getForumComments(id)]);
      setPost(p.data);
      setComments(c.data || []);
    })();
  }, [id]);

  const submit = async (e) => {
    e.preventDefault();
    if (!comment.trim()) return;
    setSubmitting(true);
    try {
      await createForumComment({ post_id: id, comment });
      setComment("");
      const res = await getForumComments(id);
      setComments(res.data || []);
      toast.success("Comment added");
    } catch (err) {
      toast.error(err.message || "Failed");
    } finally {
      setSubmitting(false);
    }
  };

  const handleLike = async () => {
    if (!user) { toast.error("Login to like posts"); return; }
    const wasLiked = post.liked;
    const prevCount = post.like_count || 0;
    setPost((p) => ({ ...p, liked: !wasLiked, like_count: prevCount + (wasLiked ? -1 : 1) }));
    try {
      const res = await toggleForumLike(post.id);
      setPost((p) => ({ ...p, liked: res.liked, like_count: res.like_count }));
    } catch (err) {
      setPost((p) => ({ ...p, liked: wasLiked, like_count: prevCount }));
      toast.error(err.message || "Failed to like post");
    }
  };

  if (!post) return <div className="p-8">Loading...</div>;

  const image = post.image_url ? resolveForumImageUrl(post.image_url) : null;

  return (
    <div className="max-w-4xl mx-auto px-4 py-8">
      <Link to="/forum" className="text-sm font-semibold text-[#00342b]">← Back to forum</Link>

      <div className="bg-white border rounded-3xl p-6 mt-4">
        <div className="flex items-center gap-3">
          <Avatar name={post.author_name} image={post.author_image} />
          <div className="min-w-0">
            <div className="font-semibold text-gray-800">{post.author_name || "Member"}</div>
            <div className="text-xs text-gray-500">{[post.author_specialty, timeAgo(post.created_at)].filter(Boolean).join(" · ")}</div>
          </div>
          <span className={`ml-auto text-[11px] font-bold uppercase tracking-wide px-2 py-0.5 rounded-full border shrink-0 ${TYPE_STYLES[post.type] || "bg-gray-50 text-gray-600 border-gray-100"}`}>
            {(post.type || "discussion").replace("_", " ")}
          </span>
        </div>

        <h1 className="text-3xl font-extrabold text-gray-900 mt-5">{post.title}</h1>
        {image && <img src={image} alt={post.title} className="mt-5 rounded-2xl w-full object-cover max-h-[420px]" />}
        <div className="prose max-w-none mt-5 whitespace-pre-line text-gray-700">{post.content}</div>

        <div className="flex items-center gap-6 mt-6 pt-5 border-t text-sm text-gray-500">
          <button
            onClick={handleLike}
            className={`flex items-center gap-2 font-semibold transition hover:text-rose-600 ${post.liked ? "text-rose-600" : ""}`}
          >
            <HeartIcon filled={post.liked} />
            {post.like_count || 0} {post.like_count === 1 ? "Like" : "Likes"}
          </button>
          <span className="flex items-center gap-2">
            <EyeIcon /> {post.views || 0} views
          </span>
          <span className="flex items-center gap-2">
            <CommentIcon /> {comments.length} {comments.length === 1 ? "reply" : "replies"}
          </span>
        </div>
      </div>

      <div className="bg-white border rounded-3xl p-6 mt-6">
        <h2 className="text-xl font-bold text-[#00342b] mb-4">Replies ({comments.length})</h2>
        <div className="space-y-3 mb-6">
          {comments.map((c) => (
            <div key={c.id} className="border rounded-2xl p-4 flex gap-3">
              <Avatar name={c.author_name} image={c.author_image} size={9} />
              <div className="min-w-0 flex-1">
                <div className="flex items-center gap-2">
                  <span className="text-sm font-semibold text-gray-800">{c.author_name}</span>
                  <span className="text-xs text-gray-400">{timeAgo(c.created_at)}</span>
                </div>
                <div className="text-sm text-gray-600 mt-1">{c.comment}</div>
              </div>
            </div>
          ))}
          {!comments.length && <p className="text-gray-500 text-sm">No replies yet. Be the first to respond.</p>}
        </div>
        {user ? (
          <form onSubmit={submit} className="space-y-3">
            <textarea className="w-full min-h-32 border rounded-2xl p-4" value={comment} onChange={(e) => setComment(e.target.value)} placeholder="Write your opinion or reply..." required />
            <button disabled={submitting} className="px-5 py-3 rounded-xl bg-[#00342b] text-white font-semibold disabled:opacity-60">
              {submitting ? "Posting…" : "Post reply"}
            </button>
          </form>
        ) : (
          <p className="text-gray-500">
            <Link to="/doctor-login" className="text-[#00342b] font-semibold underline">Login</Link> to reply.
          </p>
        )}
      </div>
    </div>
  );
}
